import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import * as turf from "@turf/turf";
import { useEffect, useMemo, useRef, useState } from "react";
import {
    getMapboxToken,
    addUpdateGeojsonSource,
    addUpdateGeojsonLayers,
} from "@/lib/utils/map";
import type {
    CircleLayerSpecification,
    SymbolLayerSpecification,
    LngLatBoundsLike,
} from "mapbox-gl";
import { NearbyCsp } from "./CspLocatorPanel";

import locationGray from "@/assets/map/location-gray.png";
import locationGreen from "@/assets/map/location-green.png";
import locationWhite from "@/assets/map/location-white.png";

const CSP_SOURCE = "csp-locations";

const CSP_ICON_LAYER: SymbolLayerSpecification = {
    id: "csp-icons",
    type: "symbol",
    source: CSP_SOURCE,
    layout: {
        "icon-image": [
            "case",
            ["==", ["get", "isElite"], true],
            "location-green",
            "location-white",
        ],
        "icon-anchor": "bottom",
        "icon-size": ["case", ["==", ["get", "isSelected"], true], 0.12, 0.08],
        "icon-allow-overlap": true,
    },
};

const CSP_LABEL_LAYER: SymbolLayerSpecification = {
    id: "csp-labels",
    type: "symbol",
    source: CSP_SOURCE,
    layout: {
        "text-field": ["get", "name"],
        "text-size": 11,
        "text-offset": [0, -3.5],
        "text-anchor": "bottom",
        "text-optional": true,
        "text-allow-overlap": false,
    },
    paint: {
        "text-color": "#ffffff",
        "text-halo-color": "#000000",
        "text-halo-width": 1,
    },
};

const USER_LOCATION_SOURCE = "user-search-location";

const USER_LOCATION_LAYER: CircleLayerSpecification = {
    id: "user-location-dot",
    type: "circle",
    source: USER_LOCATION_SOURCE,
    paint: {
        "circle-radius": 8,
        "circle-color": "#3b82f6",
        "circle-stroke-width": 3,
        "circle-stroke-color": "#ffffff",
    },
};

export default function CspLocatorMap({
    csps,
    coords,
    selectedCspId,
    setSelectedCspId,
    cardRefs,
}: {
    csps: NearbyCsp[];
    coords: { lat: number; lon: number } | null;
    selectedCspId: number | null;
    setSelectedCspId: React.Dispatch<React.SetStateAction<number | null>>;
    cardRefs: React.MutableRefObject<Map<number, HTMLDivElement>>;
}) {
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const [mapLoaded, setMapLoaded] = useState(false);

    useEffect(() => {
        if (!mapContainerRef.current) return;

        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: "mapbox://styles/mapbox/satellite-streets-v11",
            center: [-96.7026, 40.8136],
            zoom: 4,
            accessToken: getMapboxToken(),
        });

        map.addControl(new mapboxgl.NavigationControl(), "top-right");
        map.addControl(
            new mapboxgl.GeolocateControl({
                positionOptions: {
                    enableHighAccuracy: true,
                },
                trackUserLocation: true,
                showUserLocation: true,
            }),
        );

        map.on("load", () => {
            const icons: Record<string, string> = {
                "location-gray": locationGray,
                "location-green": locationGreen,
                "location-white": locationWhite,
            };

            Object.entries(icons).forEach(([name, img]) => {
                map.loadImage(img, (error, image) => {
                    if (error) throw error;
                    if (!map.hasImage(name) && image) {
                        map.addImage(name, image);
                    }
                });
            });

            setMapLoaded(true);
        });

        mapRef.current = map;

        return () => {
            setMapLoaded(false);
            map.remove();
            mapRef.current = null;
        };
    }, []);

    const cspGeojson = useMemo(() => {
        const features = csps
            .filter((c) => c.location_lat != null && c.location_lon != null)
            .map((c) => {
                const isElite =
                    c.organization.csp_preference === "preferred" &&
                    c.distance_miles != null &&
                    c.distance_miles <= 30;

                return {
                    type: "Feature" as const,
                    geometry: {
                        type: "Point" as const,
                        coordinates: [c.location_lon!, c.location_lat!],
                    },
                    properties: {
                        id: c.organization.id,
                        name: c.organization.name,
                        isElite,
                        isSelected: c.organization.id === selectedCspId,
                    },
                };
            });

        return turf.featureCollection(features);
    }, [csps, selectedCspId]);

    useEffect(() => {
        if (!mapRef.current || !mapLoaded) return;

        addUpdateGeojsonSource(mapRef.current, CSP_SOURCE, {
            type: "geojson",
            data: cspGeojson,
        });
        addUpdateGeojsonLayers(mapRef.current, [
            CSP_ICON_LAYER,
            CSP_LABEL_LAYER,
        ]);
    }, [cspGeojson, mapLoaded]);

    useEffect(() => {
        if (!mapRef.current || !mapLoaded || !csps.length) return;

        const boundsFeatures = csps
            .filter((c) => c.location_lat != null && c.location_lon != null)
            .map((c) => turf.point([c.location_lon!, c.location_lat!]));

        if (coords) {
            boundsFeatures.push(turf.point([coords.lon, coords.lat]));
        }

        if (boundsFeatures.length) {
            mapRef.current.fitBounds(
                turf.bbox(
                    turf.featureCollection(boundsFeatures),
                ) as LngLatBoundsLike,
                { padding: 60, duration: 500, maxZoom: 12 },
            );
        }
    }, [csps, mapLoaded, coords]);

    useEffect(() => {
        if (!mapRef.current || !mapLoaded || !coords) return;

        addUpdateGeojsonSource(mapRef.current, USER_LOCATION_SOURCE, {
            type: "geojson",
            data: turf.featureCollection([
                turf.point([coords.lon, coords.lat]),
            ]),
        });
        addUpdateGeojsonLayers(mapRef.current, [USER_LOCATION_LAYER]);
    }, [coords, mapLoaded]);

    useEffect(() => {
        if (!mapRef.current || !mapLoaded) return;
        const map = mapRef.current;

        const onClick = (e: mapboxgl.MapMouseEvent) => {
            const features = map.queryRenderedFeatures(e.point, {
                layers: [CSP_ICON_LAYER.id],
            });
            if (features?.length) {
                const id = features[0].properties?.id as number;
                const coords = features[0].geometry as unknown as { coordinates: [number, number] };
                setSelectedCspId((prev) => (prev === id ? null : id));
                map.flyTo({
                    center: coords.coordinates,
                    zoom: 10,
                    duration: 1500,
                });
                const el = cardRefs.current.get(id);
                el?.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        };

        map.on("click", CSP_ICON_LAYER.id, onClick);
        const onMouseEnter = () => { map.getCanvas().style.cursor = "pointer"; };
        const onMouseLeave = () => { map.getCanvas().style.cursor = ""; };

        map.on("mouseenter", CSP_ICON_LAYER.id, onMouseEnter);
        map.on("mouseleave", CSP_ICON_LAYER.id, onMouseLeave);

        return () => {
            map.off("click", CSP_ICON_LAYER.id, onClick);
            map.off("mouseenter", CSP_ICON_LAYER.id, onMouseEnter);
            map.off("mouseleave", CSP_ICON_LAYER.id, onMouseLeave);
        };
    }, [mapLoaded, setSelectedCspId, cardRefs]);

    return <div ref={mapContainerRef} className="w-full h-full" />;
}
